import {
  Attachment,
  Emoji,
  Id,
  MembersData,
  Message,
  Resource,
} from '@nextforce/api'
import { useNetInfo } from '@react-native-community/netinfo'
import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useChatConfig } from 'chat/config/queries/get'
import { useCreateDirectConversation } from 'chat/conversations/queries/create_direct'
import { useConversation } from 'chat/conversations/queries/detail'
import { useUpdateConversation } from 'chat/conversations/queries/update'
import { useUpdateConversationLastViewed } from 'chat/conversations/timestamps'
import { useMembers } from 'chat/members/queries/list'
import { useCreateReadReceipts } from 'chat/messages/readers/queries/create'
import MessagesView from 'chat/messages/views'
import { useSendMessageContext } from 'chat/send_message_context'
import * as Clipboard from 'expo-clipboard'
import { MessagesScreenProps } from 'home/stack/types'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'
import { showHeaderTitle } from 'shared/header/header_title'
import { openLink, stripInvalidCodepoints } from 'shared/utils'
import { useUser } from 'users/queries/detail'
import { isPdfAttachment } from './file/screen'
import { useCreateReaction } from './queries/create_reaction'
import { useDeleteMessage } from './queries/delete'
import { useLoadEarlierConversationMessages, useMessages } from './queries/list'
import { usePinnedMessages } from './queries/list_pins'
import { useMarkMessage } from './queries/mark'
import { usePinMessage } from './queries/pin'
import { useReadByCounts } from './queries/read_by_count'
import { useUnmarkMessage } from './queries/unmark'
import { useUnpinMessage } from './queries/unpin'

export function MessagesScreen({ navigation, route }: MessagesScreenProps) {
  const { theme } = useTheme()
  const { handleNavigation } = useRoutingContext()
  const conversationId = route.params.id
  const { data: user } = useUser()
  const { loadEarlier, hasMore: hasMoreMessages } =
    useLoadEarlierConversationMessages(conversationId)

  const {
    data: chatConfig,
    isLoading: isChatConfigLoading,
    error: chatConfigError,
  } = useChatConfig()
  const {
    data: conversationMessages,
    isLoading: isLoadingMessages,
    isRefetching: isRefetchingMessages,
    error: messagesError,
  } = useMessages(
    data =>
      data.messages.filter(
        message => message.conversationId === conversationId
      ) ?? []
  )

  const {
    data: conversation,
    isLoading: isConversationLoading,
    error: conversationError,
  } = useConversation(conversationId)

  const conversationUpdateMutation = useUpdateConversation(conversationId)

  const pinnedMessagesQuery = usePinnedMessages(conversationId).data
  const prefillMessageText =
    route.params.message && decodeURIComponent(route.params.message)

  const createDirectConversationMutation = useCreateDirectConversation()
  const createReactionMutation = useCreateReaction()
  const deleteMessageMutation = useDeleteMessage()
  const pinMessageMutation = usePinMessage(conversationId)
  const unpinMessageMutation = useUnpinMessage(conversationId)
  const membersQuery = useMembers(conversationId, true)

  const markMessageMutation = useMarkMessage()
  const unmarkMessageMutation = useUnmarkMessage()
  const showNewsfeedLayout = conversation?.type === 'companyAnnouncements'

  const readByCountsQuery = useReadByCounts(conversationId, true)

  const [shouldShowRefetchLoading, setShouldShowRefetchLoading] = useState(
    route.params.showLoading ?? false
  )
  const [isMessagesLoading, setIsMessagesLoading] = useState(
    (shouldShowRefetchLoading && isRefetchingMessages) || isLoadingMessages
  )
  const { mutateAsync: conversationUpdateLastViewed } =
    useUpdateConversationLastViewed(conversationId)
  const { mutateAsync: createReadReceipts } = useCreateReadReceipts(
    conversationId,
    true
  )
  const {
    getMessageBody,
    setMessageBody,
    getSelectedAttachments,
    getFileUploadStatus,
    onSendMessage,
    pasteAttachments,
    getReplyMessage,
    setReplyMessage,
  } = useSendMessageContext()
  const [isLoadingEarlier, setIsLoadingEarlier] = useState(false)

  // Fire loadEarlier once on initial mount to initialize hasMoreMessages
  useEffect(() => {
    if (hasMoreMessages === undefined && !isRefetchingMessages) {
      setIsLoadingEarlier(true)
      loadEarlier()
        .catch(err => {
          null
        })
        .finally(() => setIsLoadingEarlier(false))
    }
  }, [hasMoreMessages, isRefetchingMessages, loadEarlier])

  useEffect(() => {
    if (shouldShowRefetchLoading) {
      setIsMessagesLoading(isRefetchingMessages || isLoadingMessages)
    } else {
      setIsMessagesLoading(isLoadingMessages)
    }
  }, [isLoadingMessages, isRefetchingMessages, shouldShowRefetchLoading])

  useEffect(() => {
    if (shouldShowRefetchLoading === true) {
      if (isMessagesLoading === false) {
        setShouldShowRefetchLoading(false)
      }
    }
  }, [isMessagesLoading, shouldShowRefetchLoading])

  const selectedAttachments = useMemo(
    () => getSelectedAttachments(conversationId),
    [conversationId, getSelectedAttachments]
  )
  const messageBody = useMemo(
    () => getMessageBody(conversationId),
    [conversationId, getMessageBody]
  )
  const replyMessageValue = useMemo(
    () => getReplyMessage(conversationId),
    [conversationId, getReplyMessage]
  )

  const isUploading = useMemo(() => {
    if (conversation) {
      return getFileUploadStatus(conversationId)?.progress !== undefined
    }
    return false
  }, [conversation, getFileUploadStatus, conversationId])

  const members = useMemo(() => {
    let conversationMembers: MembersData = []

    if (membersQuery.data) {
      conversationMembers = membersQuery.data
    }
    return conversationMembers
  }, [membersQuery.data])

  const setChatMessageBody = useCallback(
    (text: string) => {
      if (conversation) {
        setMessageBody(conversation.id, text)
      }
    },
    [conversation, setMessageBody]
  )

  useLayoutEffect(() => {
    showHeaderTitle(
      navigation,
      stripInvalidCodepoints(conversation?.name ?? t('messages'))
    )

    showHeaderButton({
      navigation,
      theme,
      title:
        conversation && conversation.type !== 'dm'
          ? 'information-circle-outline'
          : undefined,
      onPress: () => {
        handleNavigation({
          routeName: 'conversation_details',
          routeParams: { conversationId },
        })
      },
      position: 'right',
      useIcon: true,
    })
  }, [conversation, conversationId, handleNavigation, navigation, theme])

  const isUpdatingLastViewedRef = useRef(false)

  const previousLastViewedTimestamp = route.params.previousLastViewedTimestamp

  const updateLastViewed = useCallback(async () => {
    if (isUpdatingLastViewedRef.current || !conversation) {
      return
    }
    isUpdatingLastViewedRef.current = true
    try {
      await conversationUpdateLastViewed()
      await createReadReceipts()
    } catch (e) {
      console.log(e)
    } finally {
      isUpdatingLastViewedRef.current = false
    }
  }, [conversation, conversationUpdateLastViewed, createReadReceipts])

  // useEffect to update last viewed on navigation
  useEffect(() => {
    updateLastViewed().catch(e => console.log(e))
  }, [updateLastViewed])

  // useEffect to update last viewed when new messages arrive
  useEffect(() => {
    if (!conversation || !conversationMessages) {
      return
    }

    const latestMessageTimestamp =
      conversationMessages.length > 0
        ? conversationMessages[conversationMessages.length - 1].createdAt
        : 0
    const conversationLastViewedTimestamp =
      conversation.status?.lastViewedTimestamp ?? 0

    if (latestMessageTimestamp > conversationLastViewedTimestamp) {
      updateLastViewed().catch(e => console.log(e))
    }
  }, [conversation, conversationMessages, updateLastViewed])

  const onSendButtonPressed = useCallback(async () => {
    await onSendMessage({ conversationId })
  }, [conversationId, onSendMessage])

  const onSetReplyMessage = useCallback(
    (message: Message | undefined) => setReplyMessage(conversationId, message),
    [conversationId, setReplyMessage]
  )

  const onShowFile = useCallback(
    (attachment: Attachment, isPinnedMessage?: boolean) => {
      if (isPdfAttachment(attachment)) {
        handleNavigation({
          routeName: 'pdf',
          routeParams: { url: attachment.uri, name: attachment.name },
        })
      } else {
        handleNavigation({
          routeName: 'file',
          routeParams: {
            ...attachment,
            conversationId: conversationId,
            isPinnedMessage,
          },
        })
      }
    },
    [conversationId, handleNavigation]
  )

  const onShowMultiAttachments = useCallback(
    (messageId: string, attachmentIndex: number) => {
      handleNavigation({
        routeName: 'file',
        routeParams: {
          messageId,
          attachmentIndex,
          conversationId,
        },
      })
    },
    [conversationId, handleNavigation]
  )

  const onRemoveMessage = useCallback(
    async (id: Id) => {
      await deleteMessageMutation.mutateAsync({
        conversationId,
        messageId: id,
      })
    },
    [conversationId, deleteMessageMutation]
  )

  const onSendReaction = useCallback(
    async (id: string, emoji: Emoji) => {
      await createReactionMutation.mutateAsync({
        conversationId,
        messageId: id,
        emoji: emoji,
      })
    },
    [conversationId, createReactionMutation]
  )

  const onPinMessage = useCallback(
    async (id: Id) => {
      await pinMessageMutation.mutateAsync({
        conversationId,
        messageId: id,
      })
    },
    [conversationId, pinMessageMutation]
  )

  const onMarkMessage = useCallback(
    async (id: Id) => {
      await markMessageMutation.mutateAsync({ conversationId, messageId: id })
    },
    [conversationId, markMessageMutation]
  )

  const onUnpinMessage = useCallback(
    async (id: Id) => {
      await unpinMessageMutation.mutateAsync({ conversationId, messageId: id })
    },
    [conversationId, unpinMessageMutation]
  )

  const onUnmarkMessage = useCallback(
    async (id: Id) => {
      await unmarkMessageMutation.mutateAsync({
        conversationId,
        messageId: id,
      })
    },
    [conversationId, unmarkMessageMutation]
  )

  const onHideDescription = useCallback(async () => {
    await conversationUpdateMutation.mutateAsync({
      changes: { descriptionHidden: true },
    })
  }, [conversationUpdateMutation])

  const onSendDirectMessage = useCallback(
    async (recipientId: string) => {
      try {
        const directConversation =
          await createDirectConversationMutation.mutateAsync(recipientId)
        navigation.push('messages', {
          id: directConversation.id,
        })
      } catch (err: any) {
        // FIXME: better send direct error message
        alert(err.message)
      }
    },
    [createDirectConversationMutation, navigation]
  )

  const onShowReaders = useCallback(
    (msgId: Id) => {
      handleNavigation({
        routeName: 'readers',
        routeParams: { conversationId, messageId: msgId },
      })
    },
    [conversationId, handleNavigation]
  )

  const onShowReactors = useCallback(
    (msgId: Id, emoji: Emoji) => {
      handleNavigation({
        routeName: 'reactors',
        routeParams: {
          conversationId,
          messageId: msgId,
          emoji,
        },
      })
    },
    [conversationId, handleNavigation]
  )

  const onCreateMentions = useCallback(
    (msgId: Id) => {
      handleNavigation({
        routeName: 'mentions',
        routeParams: {
          conversationId,
          messageId: msgId,
        },
      })
    },
    [conversationId, handleNavigation]
  )

  const onCreateComposeMessage = useCallback(() => {
    handleNavigation({
      routeName: 'compose',
      routeParams: {
        conversationId: conversationId,
        name: conversation?.name,
        type: 'messageGroup',
      },
    })
  }, [conversation?.name, conversationId, handleNavigation])

  const onCopyMessage = useCallback(async (message?: Message) => {
    if (!message) {
      return
    }

    if (message.body) {
      Clipboard.setString(message.body)
    } else if (message.attachment) {
      //TODO: [N3-2100] Handle copy paste attachements
    }
  }, [])

  const clearScrollPosition = useCallback(() => {
    navigation.setParams({ messageId: undefined })
  }, [navigation])

  const netInfo = useNetInfo()

  const error = user?.chat?.enabled
    ? messagesError ?? conversationError ?? chatConfigError
    : new Error(t('chatNotAvailable'))

  const onLinkPress = useCallback(
    async (url: string) => {
      if (user) {
        await openLink(url, navigation, user)
      }
    },
    [navigation, user]
  )

  const onPressAcknowledgeResource = useCallback(
    (resource: Resource) => {
      handleNavigation({
        routeName: 'acknowledge_resource',
        routeParams: { resourceId: resource.id, resourceType: resource.type },
      })
    },
    [handleNavigation]
  )

  const onLoadEarlier = useCallback(async () => {
    setIsLoadingEarlier(true)

    try {
      await loadEarlier()
    } catch (err) {
    } finally {
      setIsLoadingEarlier(false)
    }
  }, [loadEarlier])

  return (
    <StatusBoundary
      isLoading={
        isConversationLoading || isMessagesLoading || isChatConfigLoading
      }
      error={error}
    >
      <MessagesView
        messages={conversationMessages}
        pinnedMessages={pinnedMessagesQuery?.pinnedMessages}
        user={user}
        conversation={conversation}
        online={netInfo.isInternetReachable ?? netInfo.isConnected ?? true}
        isNewsfeedLayout={showNewsfeedLayout}
        readByCounts={readByCountsQuery.data}
        prefillMessageText={prefillMessageText}
        chatConfig={chatConfig}
        onSendMessage={onSendButtonPressed}
        onSendReaction={onSendReaction}
        onShowFile={onShowFile}
        onRemoveMessage={onRemoveMessage}
        onShowReaders={onShowReaders}
        onHideDescription={onHideDescription}
        onSendDirectMessage={onSendDirectMessage}
        onCopyMessage={onCopyMessage}
        onShowReactors={onShowReactors}
        onCreateMentions={onCreateMentions}
        onUnpinMessage={onUnpinMessage}
        onPinMessage={onPinMessage}
        onUnmarkMessage={onUnmarkMessage}
        onMarkMessage={onMarkMessage}
        messageId={route.params.messageId}
        onClearScrolling={clearScrollPosition}
        onLinkPress={onLinkPress}
        onCreateComposeMessage={onCreateComposeMessage}
        onShowMultiAttachments={onShowMultiAttachments}
        messageBody={messageBody}
        selectedAttachments={selectedAttachments}
        setChatMessageBody={setChatMessageBody}
        pasteAttachments={pasteAttachments}
        isUploading={isUploading}
        onPressAcknowledgeResource={onPressAcknowledgeResource}
        members={members}
        replyMessage={replyMessageValue}
        setReplyMessage={onSetReplyMessage}
        previousLastViewedTimestamp={previousLastViewedTimestamp}
        isLoadingEarlier={isLoadingEarlier}
        hasMoreMessages={hasMoreMessages}
        onLoadEarlier={onLoadEarlier}
      />
    </StatusBoundary>
  )
}
